import { AiAgentWbsItemRef } from '../lib/functions/ai'

// AIエージェントチャットへ添付できるWBSアイテム参照の上限。上限を超える分は追加しない
// （呼び出し側はこの定数を使って、超過した旨をユーザーに伝えるかどうかを判定する）。
export const MAX_WBS_ITEM_ATTACHMENTS = 50

enum ActionType {
  ADD_WBS_ITEM_ATTACHMENTS = 'aiAgentChat/ADD_WBS_ITEM_ATTACHMENTS',
  SET_WBS_ITEM_ATTACHMENTS = 'aiAgentChat/SET_WBS_ITEM_ATTACHMENTS',
  REMOVE_WBS_ITEM_ATTACHMENT = 'aiAgentChat/REMOVE_WBS_ITEM_ATTACHMENT',
  CLEAR_WBS_ITEM_ATTACHMENTS = 'aiAgentChat/CLEAR_WBS_ITEM_ATTACHMENTS',
  OPEN_CHAT_PANEL = 'aiAgentChat/OPEN_CHAT_PANEL',
  CLOSE_CHAT_PANEL = 'aiAgentChat/CLOSE_CHAT_PANEL',
  TOGGLE_CHAT_PANEL = 'aiAgentChat/TOGGLE_CHAT_PANEL',
}

type State = {
  // 画面側からpushされた、送信前のWBSアイテム添付一覧。
  wbsItemAttachments: AiAgentWbsItemRef[]
  // チャットパネルを開いているか。ヘッダーの起動ボタンと画面側からの添付操作の
  // どちらからも開閉するため、パネル自身のローカル状態ではなくここで持つ。
  isPanelOpen: boolean
}

const initialState: State = {
  wbsItemAttachments: [],
  isPanelOpen: false,
}

export const addWbsItemAttachments = (items: AiAgentWbsItemRef[]) => ({
  type: ActionType.ADD_WBS_ITEM_ATTACHMENTS as const,
  payload: items,
})

// 送信待ち行列の項目を編集するときに、その項目の添付で入力欄の添付を置き換える。
export const setWbsItemAttachments = (items: AiAgentWbsItemRef[]) => ({
  type: ActionType.SET_WBS_ITEM_ATTACHMENTS as const,
  payload: items,
})

export const removeWbsItemAttachment = (code: string) => ({
  type: ActionType.REMOVE_WBS_ITEM_ATTACHMENT as const,
  payload: code,
})

export const clearWbsItemAttachments = () => ({
  type: ActionType.CLEAR_WBS_ITEM_ATTACHMENTS as const,
})

export const openChatPanel = () => ({
  type: ActionType.OPEN_CHAT_PANEL as const,
})

export const closeChatPanel = () => ({
  type: ActionType.CLOSE_CHAT_PANEL as const,
})

export const toggleChatPanel = () => ({
  type: ActionType.TOGGLE_CHAT_PANEL as const,
})

type Action =
  | ReturnType<typeof addWbsItemAttachments>
  | ReturnType<typeof setWbsItemAttachments>
  | ReturnType<typeof removeWbsItemAttachment>
  | ReturnType<typeof clearWbsItemAttachments>
  | ReturnType<typeof openChatPanel>
  | ReturnType<typeof closeChatPanel>
  | ReturnType<typeof toggleChatPanel>

// 追加対象のうち、まだ添付されていないもの（code重複なし）だけを抽出する。
const pickNewItems = (
  current: AiAgentWbsItemRef[],
  toAdd: AiAgentWbsItemRef[]
): AiAgentWbsItemRef[] => {
  const existingCodes = new Set(current.map(item => item.code))
  return toAdd.filter(item => !existingCodes.has(item.code))
}

// 上限を超えて追加されなかった件数。0より大きい場合、呼び出し側はユーザーに通知する
// （重複除去だけで結果的に追加0件になったケースは上限超過ではないため区別する）。
export const countWbsItemAttachmentsRejectedByLimit = (
  current: AiAgentWbsItemRef[],
  toAdd: AiAgentWbsItemRef[]
): number => {
  const newItems = pickNewItems(current, toAdd)
  const acceptableCount = Math.max(0, MAX_WBS_ITEM_ATTACHMENTS - current.length)
  return Math.max(0, newItems.length - acceptableCount)
}

export const reducer = (state: State = initialState, action: Action): State => {
  switch (action.type) {
    case ActionType.ADD_WBS_ITEM_ATTACHMENTS: {
      const newItems = pickNewItems(state.wbsItemAttachments, action.payload)
      const acceptableCount = Math.max(
        0,
        MAX_WBS_ITEM_ATTACHMENTS - state.wbsItemAttachments.length
      )
      if (newItems.length === 0 || acceptableCount === 0) {
        return state
      }
      return {
        ...state,
        wbsItemAttachments: [
          ...state.wbsItemAttachments,
          ...newItems.slice(0, acceptableCount),
        ],
      }
    }

    case ActionType.SET_WBS_ITEM_ATTACHMENTS:
      return {
        ...state,
        wbsItemAttachments: action.payload.slice(0, MAX_WBS_ITEM_ATTACHMENTS),
      }

    case ActionType.REMOVE_WBS_ITEM_ATTACHMENT:
      return {
        ...state,
        wbsItemAttachments: state.wbsItemAttachments.filter(
          item => item.code !== action.payload
        ),
      }

    case ActionType.CLEAR_WBS_ITEM_ATTACHMENTS:
      return { ...state, wbsItemAttachments: [] }

    case ActionType.OPEN_CHAT_PANEL:
      return { ...state, isPanelOpen: true }

    case ActionType.CLOSE_CHAT_PANEL:
      return { ...state, isPanelOpen: false }

    case ActionType.TOGGLE_CHAT_PANEL:
      return { ...state, isPanelOpen: !state.isPanelOpen }

    default:
      return state
  }
}

// Selectors
export const selectWbsItemAttachments = (state: {
  aiAgentChat: State
}): AiAgentWbsItemRef[] => state.aiAgentChat.wbsItemAttachments

export const selectIsPanelOpen = (state: { aiAgentChat: State }): boolean =>
  state.aiAgentChat.isPanelOpen
